import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from 'wouter';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { registerSchema } from '@shared/schema';
import type { RegisterInput } from '@shared/schema';
import { Navbar } from '@/components/Navbar';
import { Link } from 'wouter';
import { z } from 'zod';
import { Separator } from '@/components/ui/separator';
import { TelegramLoginButton } from '@/components/TelegramLoginButton';
import { useIsMobile } from '@/hooks/use-mobile';
import { PasswordInput } from '@/components/ui/password-input';

const extendedRegisterSchema = registerSchema.extend({
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ExtendedRegisterInput = z.infer<typeof extendedRegisterSchema>;

export default function Register() {
  const [, setLocation] = useLocation();
  const { register } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Get redirect parameter from URL
  const redirectTo = new URLSearchParams(window.location.search).get('redirect');

  const form = useForm<ExtendedRegisterInput>({
    resolver: zodResolver(extendedRegisterSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: ExtendedRegisterInput) => {
    try {
      await register(data.email, data.password);
      toast({
        title: 'Success!',
        description: 'Your account has been created. Welcome!',
      });

      // Redirect based on where the user came from
      if (redirectTo === 'pricing') {
        window.location.href = '/#pricing';
      } else if (redirectTo === 'payment' || redirectTo === 'subscribe') {
        setLocation('/subscribe');
      } else {
        setLocation('/');
      }
    } catch (error) {
      toast({
        title: 'Registration Failed',
        description: error instanceof Error ? error.message : 'An error occurred during registration',
        variant: 'destructive',
      });
    }
  };

  const passwordRequirements = [
    { label: 'At least 8 characters', met: form.watch('password')?.length >= 8 },
    {
      label: 'At least one uppercase letter',
      met: /[A-Z]/.test(form.watch('password') || ''),
    },
    {
      label: 'At least one lowercase letter',
      met: /[a-z]/.test(form.watch('password') || ''),
    },
    { label: 'At least one number', met: /[0-9]/.test(form.watch('password') || '') },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-20 pb-10 px-4 flex items-center justify-center">
        <Card className="glass w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl">Create Account</CardTitle>
            <CardDescription>Join us to start receiving betting tips</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="you@example.com"
                          {...field}
                          disabled={form.formState.isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <PasswordInput
                          placeholder="••••••••"
                          {...field}
                          disabled={form.formState.isSubmitting}
                        />
                      </FormControl>
                      <div className="mt-2 space-y-1">
                        {passwordRequirements.map((req) => (
                          <p
                            key={req.label}
                            className={`text-xs ${
                              req.met ? 'text-primary' : 'text-muted-foreground'
                            }`}
                          >
                            {req.met ? '✓' : '○'} {req.label}
                          </p>
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <PasswordInput
                          placeholder="••••••••"
                          {...field}
                          disabled={form.formState.isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full font-bold"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Create Account
                </Button>
              </form>
            </Form>

            <div className="my-6 flex items-center gap-4">
              <Separator className="flex-1" />
              <span className="text-sm text-muted-foreground">or</span>
              <Separator className="flex-1" />
            </div>

            <div className="flex justify-center">
              <TelegramLoginButton buttonSize={isMobile ? 'medium' : 'large'} />
            </div>

            <div className="mt-4 text-center text-sm">
              <p className="text-muted-foreground">
                Already have an account?{' '}
                <Link href="/login" className="text-primary hover:underline cursor-pointer">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
